import {
  UilPlus,
  UilPen,
  UilTrash,
  UilImport,
  UilUpload,
} from '@iconscout/react-unicons';
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  NumberField,
  Divider,
  TextField,
  IconButton,
  Notification,
  ConfirmDialog,
  Waiting,
  Box,
} from '@xbotvn/mui';
import React, {
  useMemo, useState,
} from 'react';
import {
  useDispatch, useSelector,
} from 'react-redux';

import { paginationApi } from '../../../../api';
import DropzoneModal from '../../../../components/DropzoneModal';
import Table from '../../../../components/Table';
import {
  LISTING,
} from '../../../../libs/config';
import {
  cloneDeep,
  getOr,
  values,
} from '../../../../libs/lodash';
import {
  FILE,
  TYPE,
} from '../../../../redux/actions/constants';
import {
  importExcel,
} from '../../Helpers';

export default function Type() {
  const dispatch = useDispatch();

  const {
    typeData,
    handling,
    activeUnit,
  } = useSelector(({
    type, user,
  }) => ({
    typeData: type.data,
    handling: type.handling,
    activeUnit: user.activeUnit,
  }));

  const [editor, setEditor] = useState();
  const [showImport, setShowImport] = useState();
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const columns = useMemo(() => [
    {
      Header: 'STT',
      style: {
        width: 100,
      },
      Cell: ({
        row,
      }) => row.index + 1,
    },
    {
      Header: 'Tên',
      accessor: 'name',
      style: {
        width: 350,
      },
    },
    {
      Header: 'Thời hạn mượn tối đa',
      accessor: 'duration',
      style: {
        width: 350,
      },
    },
    {
      Header: 'Số lượng mượn tối đa',
      accessor: 'maxAmount',
      style: {
        width: 350,
      },
    },
    {
      Header: () => (
        <Button
          startIcon={<UilPlus />}
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => setEditor({
            unitId: activeUnit,
            name: '',
          })}
        >
          Thêm
        </Button>
      ),
      id: 'add',
      style: {
        width: 250,
      },
      Cell: (rowProps) => {
        const rowValue = getOr({}, 'row.original', rowProps);
        return (
          <ButtonGroup>
            <IconButton
              onClick={() => setEditor(cloneDeep(rowValue))}
              color="primary"
              size="small"
              sx={{ marginRight: '5px' }}
            >
              <UilPen />
            </IconButton>
            <Divider orientation="vertical" flexItem />

            <IconButton
              color="error"
              size="small"
              sx={{ marginLeft: '5px' }}
              onClick={() => {
                setRemoveConfirm(rowValue._id);
              }}
            >
              <UilTrash />
            </IconButton>
          </ButtonGroup>
        );
      },
    },
  ], []);

  const renderEditor = () => (
    <Dialog
      open
      onClose={() => setEditor()}
    >
      <DialogTitle onClose={() => setEditor()}>{editor._id ? 'Sửa' : 'Thêm'}</DialogTitle>
      <DialogContent>
        <TextField
          required
          value={editor.name || ''}
          label="Tên"
          fullWidth
          onChange={(e) => setEditor((prev) => ({
            ...prev,
            name: e.target.value,
          }))}
        />
        <div>
          <NumberField
            label="Thời hạn mượn tối đa"
            required
            value={getOr(15, 'duration', editor)}
            onValueChange={({ floatValue }) => setEditor((prev) => ({
              ...prev,
              duration: floatValue,
            }))}
          />
          <NumberField
            buttonPosition="none"
            value={getOr(5, 'maxAmount', editor)}
            onValueChange={({ floatValue }) => setEditor((prev) => ({
              ...prev,
              maxAmount: floatValue,
            }))}
            required
            label="Số lượng mượn tối đa"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="success"
          onClick={() => {
            if (!editor.name) {
              Notification.warn('Tên không được bỏ trống.');
              return;
            }
            dispatch({
              type: TYPE.handlers.update,
              data: editor,
              onSuccess: () => setEditor(),
            });
          }}
        >
          Lưu
        </Button>
        <Button
          color="error"
          onClick={() => setEditor()}
        >
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div>
      {editor ? renderEditor() : null}

      {removeConfirm ? (
        <ConfirmDialog
          title="Bạn có chắc chắn muốn xóa?"
          color="error"
          confirmLabel="Xoá"
          cancelLabel="Huỷ"
          maxWidth="sm"
          fullWidth
          open
          onClose={() => setRemoveConfirm()}
          onConfirm={async () => {
            setWaiting(true);
            const { data } = await paginationApi.getCount('document', {
              condition: {
                unitId: activeUnit,
                typeId: removeConfirm,
                _id: { $ne: removeConfirm },
              },
            });
            const {
              count,
            } = data;
            if (count) {
              setWaiting(false);
              Notification.warn(`Có ${count} tài liệu đang sử dụng loại sách này.`);
            } else {
              dispatch({
                type: TYPE.handlers.remove,
                _id: removeConfirm,
              });
              setWaiting(false);
            }
            setRemoveConfirm();
          }}
        >
          <DialogContent />
        </ConfirmDialog>
      ) : null}
      {(handling || waiting) && <Waiting message="Đang tải dữ liệu ..." />}
      {showImport && (
        <DropzoneModal
          onConfirm={(files) => {
            if (!files.length) return;
            importExcel({
              files,
              activeUnit,
              config: LISTING,
              onSuccess: (records) => {
                dispatch({
                  type: TYPE.handlers.import,
                  data: records,
                });
                setShowImport();
              },
            });
          }}
          onClose={() => setShowImport()}
        />
      )}
      <Box marginLeft={1}>
        <Button
          startIcon={<UilUpload />}
          size="small"
          color="success"
          onClick={() => setShowImport(true)}
          sx={{
            marginRight: 1,
          }}
        >
          Nhập từ Excel
        </Button>
        <Button
          startIcon={<UilImport />}
          color="primary"
          size="small"
          onClick={() => dispatch({
            type: FILE.handlers.download,
            data: {
              unitId: activeUnit,
              fileName: 'Mau_LoaiSach.xlsx',
            },
          })}
        >
          Tải file mẫu
        </Button>

      </Box>
      <Table columns={columns} data={values(typeData)} alwaysShowPagination />
    </div>
  );
}
